import axios from 'axios';
import handler from './api-handler';

const api = axios.create();

// cancellation - https://github.com/axios/axios#cancellation
const CancelToken = axios.CancelToken;

export default class {
    constructor(route) {
        this.route = route;
        this.host = null;
        this.cancelHandler = null;
    }

    /**
     * change the route of this ApiService instance
     * @param {string} route new route path to use
     */
    setRoute(route) {
        this.route = route;
    }

    /**
     * manually change the host of this ApiService instance
     * @param {string} host new host path to use
     */
    setHost(host) {
        this.host = host;
        this.route = `${host}${this.route}`;
    }

    /**
     * send get request and send any parameters as url queries
     * @param {Object<string, string>} params any url queries to be appended to the route path
     * @param {?string} slug slug to be appended to the route path
     * @param {?Object<string, any>} config axios configuration object - https://github.com/axios/axios#request-config
     */
    query(params, slug = null, config = {}) {
        const route = slug ? `${this.route}/${slug}` : this.route;
        config.cancelToken = this.cancelToken;

        return handler(
            api.get.bind(this, route, { params, config })
        );
    }

    /**
     * send get request and append a slug
     * @param {string} route route path of the http request
     * @param {?string} slug slug to be appended to the route path
     * @param {?Object<string, any>} config axios configuration object - https://github.com/axios/axios#request-config
     */
    get(slug = '', config = {}) {
        return handler(
            api.get.bind(this, `${this.route}/${slug}`, {
                cancelToken: this.cancelToken,
                ...config
            })
        );
    }

    /**
     * send post request with parameters
     * @param {string} route route path of the http request
     * @param {Object<string, string>} params parameters to be added to the request body
     * @param {?Object<string, any>} config axios configuration object - https://github.com/axios/axios#request-config
     */
    post(params, config = {}) {
        return handler(
            api.post.bind(this, this.route, params, {
                cancelToken: this.cancelToken,
                ...config
            })
        );
    }

    /**
     * send put request with parameters
     * @param {string} route route path of the http request
     * @param {Object<string, string>} params parameters to be added to the request body
     * @param {?Object<string, any>} config axios configuration object - https://github.com/axios/axios#request-config
     */
    put(params, config = {}) {
        return handler(
            api.put.bind(this, this.route, params, {
                cancelToken: this.cancelToken,
                ...config
            })
        );
    }

    /**
     * send delete request with parameters
     * @param {string} route route path of the http request
     * @param {Object<string, string>} params parameters to be added to the request body
     * @param {?Object<string, any>} config axios configuration object - https://github.com/axios/axios#request-config
     */
    delete(params, config = {}) {
        return handler(
            api.delete.bind(this, this.route, {
                data: params,
                cancelToken: this.cancelToken,
                ...config
            })
        );
    }

    /**
     * cancels the currently active request
     */
    cancel() {
        this.cancelHandler(`Request at ${this.route} was canceled`);
    }

    /**
     * generate and return a new cancel token for each request
     * - set as a getter to simulate a private Class property (can't be overwritten)
     */
    get cancelToken() {
        return new CancelToken(executor => (this.cancelHandler = executor));
    }
}
